import Icon from '../common/Icon.jsx';

/**
 * OracleHeader — pinned hero strip for the chat screen.
 *
 * Visually distinct from the standard ScreenHeader used on News / Results:
 *   - Carries a status line that states what actually answers today (live data +
 *     knowledge base). It becomes an "AI" claim only once the hosted model ships.
 *   - Branded with the F1 Oracle mark so users understand this is an automated
 *     assistant, not a human concierge.
 */
export default function OracleHeader({ onClear, onOpenAiSettings, hasMessages }) {
  return (
    <div
      className="sticky top-0 z-30 -mx-5 mb-3 border-b border-stroke/60 bg-bg-base/85 px-5 pb-4 pt-4 backdrop-blur-md backdrop-saturate-150"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-f1-red to-f1-redDeep shadow-glow-red-soft">
            <Icon name="oracle" size={22} className="text-white" />
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-bg-base bg-signal-green"
              aria-label="Oracle online"
            />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold leading-tight text-ink-primary">
              F1 Oracle
            </h1>
            {/*
              Honest status: on web/desktop the engine is live data + a curated
              knowledge base, not an LLM — so it does not claim "Powered by AI"
              here. Flip this to name the model once the hosted backend ships.
            */}
            <p className="truncate text-2xs uppercase tracking-widest-plus text-signal-green">
              ● Live F1 data &amp; knowledge
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={onOpenAiSettings}
            aria-label="Open Oracle AI settings"
            className="pressable flex h-8 w-8 items-center justify-center rounded-full bg-bg-card2 text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
          >
            <Icon name="settings" size={15} />
          </button>
          {hasMessages ? (
            <button
              onClick={onClear}
              className="pressable rounded-full bg-bg-card2 px-3 py-1.5 text-2xs font-semibold text-ink-secondary ring-1 ring-stroke hover:text-ink-primary"
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
